import { useEffect, useState } from "react";
import { useLocation } from "@/lib/router-compat";
import { AppSidebar } from "./AppSidebar";
import { Header } from "./Header";
import { SiteFooter } from "./SiteFooter";
import { BottomNav } from "./BottomNav";
import { useDailyCoinClaim } from "@/hooks/useCoins";
import { MissingKeysDebugPanel } from "@/components/i18n/MissingKeysDebugPanel";

import { reportThirdPartyImpact } from "@/lib/thirdPartyPerf";
import { TranslationErrorNotice } from "@/components/i18n/TranslationErrorNotice";
import { ContextualEquityLinks } from "@/components/seo/ContextualEquityLinks";
import { AdFill } from "@/components/ads/AdFill";
import { LazySection } from "@/components/shared/LazySection";
import { adsAllowedOnRoute } from "@/lib/adUnits";
import { InstallAppPrompt } from "@/components/pwa/InstallAppPrompt";
import { OfflineBanner } from "@/components/pwa/OfflineBanner";
import { initPwa } from "@/lib/pwa/register";



interface MainLayoutProps {
  children: React.ReactNode;
}

export function MainLayout({ children }: MainLayoutProps) {
  // Auto-grant the daily free coin allowance once per signed-in session.
  useDailyCoinClaim();


  // Per-route third-party impact accounting → analytics `third_party_loaded`.
  const location = useLocation();
  useEffect(() => {
    initPwa();
  }, []);
  useEffect(() => {
    reportThirdPartyImpact(location.pathname);
  }, [location.pathname]);


  // Mobile navigation drawer state is owned here so the Header (trigger) and the
  // AppSidebar (drawer) stay in sync without a floating overlay button.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Ads are excluded on sign-in, payment, account, admin and bookmaker routes.
  const adsEligible = adsAllowedOnRoute(location.pathname);

  return (
    <div className="min-h-screen flex w-full bg-background overflow-x-hidden">
      <AppSidebar mobileOpen={mobileNavOpen} onMobileOpenChange={setMobileNavOpen} />

      {/* min-w-0 lets this flex column shrink to the viewport so wide tables
          scroll inside <main> instead of stretching the whole page on mobile. */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        <Header onMenuClick={() => setMobileNavOpen(true)} />

        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">
          <TranslationErrorNotice />
          {/* Header leaderboard — above the page content on tablet and desktop.
              On phones it is suppressed: a 120px reserved ad block above the H1
              pushed the headline and CTA off the first screen, which hurts both
              mobile UX and mobile LCP. Phone visitors still get the in-content
              and secondary units below. */}
          {adsEligible && (
            <div className="hidden md:block">
              <AdFill unit="header" className="mt-0 border-t-0 pt-0" />
            </div>
          )}
          {children}
          {/* Consistent, policy-compliant ad placements for every page template:
              one in-content unit after the page's own content, one secondary unit
              lower down. Both sit outside interactive cards, carry a visible
              label and a reserved height, and never render on account, payment,
              admin or auth routes (see AD_EXCLUDED_PREFIXES). */}
          {adsEligible && <AdFill unit="inContent" />}
          {/* Contextual equity funnel: in-content prose links from our
              highest-authority pages out to weaker URLs. Self-gates to the
              curated source pages. */}
          <div className="mt-10">
            <ContextualEquityLinks />
          </div>
          {/* Ad density capped for AdSense: at most 3 AdSense units per page
              (header, in-content, footer), always below the page's own content.
              AdSense is the only ad network. */}
        </main>

        {/* Final space, directly above the sitewide link block. */}
        {adsEligible && (
          <div className="px-4 sm:px-6 lg:px-8">
            <LazySection minHeight={280} rootMargin="600px 0px">
              <AdFill unit="footer" />
            </LazySection>
          </div>
        )}



        {/* Sitewide internal-link graph: a compact, crawlable link block that
            sits directly above the single site footer. Guarantees every public
            page links to the pillar sections plus the trust pages (privacy,
            terms, about, contact) that AdSense crawlers look for. */}

        <div className="pb-16 md:pb-0">
          <SiteFooter />
        </div>
      </div>
      <OfflineBanner />
      <InstallAppPrompt />
      <BottomNav />

      {import.meta.env.DEV && <MissingKeysDebugPanel />}
    </div>
  );
}

